import { useState } from "react";
import { Link, useParams } from "react-router";
import { getCertificate } from "@/api/client";
import type { Certificate, Constellation } from "@/api/types";
import { useAsync } from "@/hooks/useAsync";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { orient } from "@/lib/constellation";
import { formatCertificateNumber, formatDate, formatPercent } from "@/lib/format";
import { Failure, Loading, Logo, RarityMark, to } from "@/site/parts";
import { Plate } from "@/site/plate";

/** The constellation as a barcode: each spot a bar at its position along the body (the shark
 * turned to face its view's way), as wide as it is large. */
function SpotSignature({ c: raw }: { c: Constellation }) {
  const c = orient(raw);
  const W = 600;
  const H = 56;
  if (c.spots.length === 0) return null;
  const xs = c.spots.map((s) => s[0]);
  const lo = Math.min(...xs);
  const span = Math.max(Math.max(...xs) - lo, 1);
  const rMax = Math.max(...c.spots.map((s) => s[2]), 1);
  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      className="barcode"
      role="img"
      aria-label={`Spot signature: ${c.spots.length} spots by horizontal position`}
      style={{ width: "100%", height: "auto", display: "block" }}
    >
      {c.spots.map(([x, y, r], i) => {
        const h = H * (0.45 + 0.55 * (1 - y / Math.max(c.height, 1)));
        return (
          <rect
            key={i}
            x={((x - lo) / span) * (W - 3)}
            y={H - h}
            width={0.8 + 2.2 * (r / rMax)}
            height={h}
          />
        );
      })}
    </svg>
  );
}

function Cell({
  label,
  children,
  span = 2,
  half = false,
}: {
  label: string;
  children: React.ReactNode;
  span?: number;
  half?: boolean;
}) {
  return (
    <div className={`sheet-cell${half ? " half" : ""}`} style={{ gridColumn: `span ${span}` }}>
      <p className="label">{label}</p>
      <div style={{ marginTop: 6, fontSize: 16 }}>{children}</div>
    </div>
  );
}

function Toolbar({ cert }: { cert: Certificate }) {
  const [copied, setCopied] = useState<string | null>(null);
  async function share() {
    const url = window.location.href;
    const title = `${cert.shark.display_name} · ${formatCertificateNumber(cert.number)}`;
    try {
      if (navigator.share) {
        await navigator.share({ title, url });
        return;
      }
      await navigator.clipboard.writeText(url);
      setCopied("Link copied");
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") return;
      setCopied(`Copy failed: ${err instanceof Error ? err.message : String(err)}`);
    }
  }
  return (
    <div
      className="no-print"
      style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}
    >
      <Link
        to={to.shark(cert.shark.id)}
        className="label link-u cert-back"
        style={{ marginRight: "auto" }}
      >
        ← {cert.shark.id} record
      </Link>
      <button type="button" className="btn btn-ghost" onClick={() => void share()}>
        Share <span className="btn-arrow">↗</span>
      </button>
      <button type="button" className="btn" onClick={() => window.print()}>
        Print <span className="btn-arrow">⎙</span>
      </button>
      <span role="status" className="label" style={{ flexBasis: "100%", textAlign: "right" }}>
        {copied}
      </span>
    </div>
  );
}

function Sheet({ cert }: { cert: Certificate }) {
  const discovery = cert.kind === "discovery";
  const shark = cert.shark;
  const c = cert.constellation;
  return (
    <article className="sheet anim-in" aria-labelledby="cert-title">
      <div className="sheet-row">
        <div
          className="sheet-cell half"
          style={{ gridColumn: "span 2", display: "flex", gap: 10, alignItems: "center" }}
        >
          <Logo />
          <span style={{ fontWeight: 600, letterSpacing: "-0.02em" }}>Gillspotter</span>
        </div>
        <div
          className="sheet-cell sheet-kind"
          data-kind={cert.kind}
          style={{ gridColumn: "span 2" }}
        >
          <p className="label" style={{ color: "inherit" }}>
            Certificate of
          </p>
          <p style={{ fontWeight: 600, fontSize: 18, letterSpacing: "-0.01em" }}>
            {discovery ? "Discovery" : "Sighting"}
          </p>
        </div>
        <div className="sheet-cell half" style={{ gridColumn: "span 2", textAlign: "right" }}>
          <p className="label">Serial</p>
          <p className="mono" style={{ fontSize: 18 }}>
            {formatCertificateNumber(cert.number)}
          </p>
        </div>
      </div>
      <div className="sheet-row">
        <div className="sheet-cell" style={{ gridColumn: "span 6", padding: "28px 18px 24px" }}>
          <p className="label">
            {discovery ? "Named individual · first record" : "Individual identified"}
          </p>
          <h1
            id="cert-title"
            className="display-2"
            style={{ marginTop: 10, fontSize: "clamp(34px, 5vw, 60px)" }}
          >
            {shark.display_name}
          </h1>
          <div
            style={{
              display: "flex",
              gap: 18,
              marginTop: 16,
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            <span className="mono">{shark.id}</span>
            <RarityMark rarity={cert.rarity} large />
          </div>
        </div>
      </div>
      <div style={{ borderBottom: "1px solid var(--rule)" }}>
        {c ? (
          <Plate
            imageUrl={cert.photo_url}
            constellation={c}
            aspect={16 / 9}
            label={`${cert.observer}'s photograph of ${shark.display_name}, ${c.spots.length} spots plotted`}
          />
        ) : (
          <img
            src={cert.photo_url}
            alt={`${cert.observer}'s photograph of ${shark.display_name}`}
            style={{ width: "100%", display: "block" }}
          />
        )}
      </div>
      <div className="sheet-row">
        <Cell label={discovery ? "Finder" : "Observer"} half>
          {cert.observer}
        </Cell>
        <Cell label="Issued" half>
          {formatDate(cert.issued_at) ?? cert.issued_at}
        </Cell>
        <Cell label="Region">{shark.regions[0] ?? "—"}</Cell>
      </div>
      <div className="sheet-row">
        <Cell label="P(same individual)" half>
          <span className="mono">
            {cert.confidence === null ? "n/a · new" : formatPercent(cert.confidence)}
          </span>
        </Cell>
        <Cell label="Matched pairs" half>
          <span className="mono">{cert.n_pairs ?? "—"}</span>
        </Cell>
        <Cell label="Encounters on record">
          <span className="mono">{shark.encounters}</span>
        </Cell>
      </div>
      <div className="sheet-row">
        <div className="sheet-cell" style={{ gridColumn: "span 4" }}>
          <p className="label" style={{ marginBottom: 10 }}>
            Spot signature · {c?.spots.length ?? 0} spots
          </p>
          {c && <SpotSignature c={c} />}
        </div>
        <div
          className="sheet-cell"
          style={{ gridColumn: "span 2", display: "grid", alignContent: "space-between", gap: 12 }}
        >
          <p className="label">Verify</p>
          <p className="mono" style={{ fontSize: 11.5, wordBreak: "break-all", lineHeight: 1.45 }}>
            gillspotter.com/certificate/{cert.id}
          </p>
        </div>
      </div>
      <p className="label" style={{ padding: "12px 18px 14px" }}>
        Identified by spot-constellation matching (Groth 1986) fused with MiewID, against the
        Gillspotter registry.
      </p>
    </article>
  );
}

export function CertificateView() {
  const { id = "" } = useParams();
  const state = useAsync(`cert:${id}`, (signal) => getCertificate(id, signal));
  const cert = state.data;
  useDocumentMeta({
    title: cert
      ? `${cert.shark.display_name} · ${formatCertificateNumber(cert.number)}`
      : "Certificate",
    image: cert?.photo_url,
  });
  if (state.status === "loading") return <Loading label="Retrieving certificate" />;
  if (state.status === "error") {
    return (
      <Failure
        error={state.error}
        title="This certificate could not be retrieved."
        onRetry={state.reload}
      />
    );
  }
  return (
    <div className="in-wrap" style={{ paddingTop: 32, display: "grid", gap: 20 }}>
      <div style={{ maxWidth: 920, width: "100%", margin: "0 auto" }}>
        <Toolbar cert={state.data} />
      </div>
      <Sheet cert={state.data} />
    </div>
  );
}
